import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  Text,
  useToast,
  Spinner,
  HStack,
  Input,
  FormControl,
  FormLabel,
  Box,
} from '@chakra-ui/react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { FiLink, FiShare } from 'react-icons/fi';
import { KMDeckService } from '../services/KMDeckService';
import { KMDeckConfig } from '../types/KMDeck';

interface KMDeckShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: KMDeckConfig | null;
}

const KMDeckShareModal: React.FC<KMDeckShareModalProps> = ({ 
  isOpen, 
  onClose, 
  config 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [displayContent, setDisplayContent] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordModified, setIsPasswordModified] = useState(false);
  const [canWebShare, setCanWebShare] = useState(false);
  const toast = useToast();
  const initialPasswordRef = useRef('');

  const generateShareUrl = useCallback(async (passwordValue?: string) => {
    if (!config) return;
    
    setIsLoading(true);
    try {
      const shareData = {
        config,
        isPasswordProtected: !!passwordValue
      };
      
      let url: string;
      if (passwordValue) {
        url = await KMDeckService.createPasswordProtectedShareUrl(shareData, passwordValue);
      } else {
        url = await KMDeckService.createShareUrlWithShortening(shareData);
      }
      
      setShareUrl(url);
      setDisplayContent(url);
    } catch (error) {
      console.error('[KMDeckShareModal] Failed to generate share URL:', error);
      toast({
        title: 'Failed to generate link',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [config, toast]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    // Only set as modified if it's different from the initial password
    if (value !== initialPasswordRef.current) {
      setIsPasswordModified(true);
    } else {
      setIsPasswordModified(false);
    }
  };

  const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isPasswordModified) {
        handleUpdateLink();
      }
    } else if (e.key === 'Escape') {
      if (isPasswordModified) {
        e.preventDefault();
        // Revert to the initial password
        setPassword(initialPasswordRef.current);
        setIsPasswordModified(false);
      }
    }
  };

  const handleUpdateLink = async () => {
    await generateShareUrl(password || undefined);
    // Update the initial password reference to the current password
    initialPasswordRef.current = password;
    setIsPasswordModified(false);
  };

  const copyToClipboard = async () => {
    try {
      // Copy exactly what's shown in the display field
      await navigator.clipboard.writeText(displayContent);
      toast({
        title: 'Success',
        description: 'Share link copied to clipboard',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const shareViaWebShare = async () => {
    if (!navigator.share) return;
    
    try {
      await navigator.share({
        title: 'KMDeck Configuration',
        text: 'Check out this KMDeck configuration',
        url: shareUrl,
      });
    } catch (error) {
      // User cancelled or error occurred
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[KMDeckShareModal] Web share error:', error);
        toast({
          title: 'Share failed',
          description: 'Failed to share via native share',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Store the initial password when modal opens
      initialPasswordRef.current = password;
      generateShareUrl(password || undefined);
      setIsPasswordModified(false);
    }
    setCanWebShare(typeof navigator !== 'undefined' && !!navigator.share);
  }, [isOpen, generateShareUrl]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text textAlign="center" fontSize="xl" fontWeight="bold">
            Share KMDeck
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            {isLoading ? (
              <HStack justify="center" py={8}>
                <Spinner />
                <Text>Generating share link...</Text>
              </HStack>
            ) : (
              <>
                <Box
                  p={3}
                  bg="gray.800"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.600"
                  h="48px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text
                    color="blue.300"
                    fontSize="sm"
                    wordBreak="break-all"
                    textAlign="center"
                    fontFamily="mono"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxW="100%"
                  >
                    {displayContent || 'Share link will appear here...'}
                  </Text>
                </Box>

                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <Input
                      value={password}
                      onChange={handlePasswordChange}
                      onKeyDown={handlePasswordKeyDown}
                      placeholder="Password (optional)"
                      size="md"
                      bg="whiteAlpha.50"
                      borderColor="accent.500"
                      _focus={{ borderColor: 'accent.500', outline: 'none' }}
                      _hover={{ borderColor: 'accent.500' }}
                      color="white"
                      _placeholder={{ color: 'gray.400' }}
                    />
                  </FormControl>

                  {isPasswordModified ? (
                    <HStack justify="center" w="full">
                      <Button
                        onClick={handleUpdateLink}
                        isLoading={isLoading}
                        loadingText="Updating..."
                        colorScheme="blue"
                        size="lg"
                        px={8}
                        py={6}
                        borderRadius="full"
                        _hover={{ bg: "blue.600" }}
                        _active={{ bg: "blue.700" }}
                      >
                        Update Link
                      </Button>
                    </HStack>
                  ) : (
                    <HStack spacing={8} justify="center" w="full">
                      <VStack spacing={1}>
                        <Button
                          variant="ghost"
                          colorScheme="blue"
                          onClick={copyToClipboard}
                          size="lg"
                          p={0}
                          w="56px"
                          h="56px"
                          borderRadius="full"
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          _hover={{ color: "accent.400", bg: "gray.800" }}
                          _active={{ color: "accent.500", bg: "gray.900" }}
                          aria-label="Copy Link"
                        >
                          <FiLink size={28} />
                        </Button>
                        <Text fontSize="xs" color="gray.400" textAlign="center">Copy</Text>
                      </VStack>
                      {canWebShare && (
                        <VStack spacing={1}>
                          <Button
                            variant="ghost"
                            colorScheme="teal"
                            onClick={shareViaWebShare}
                            size="lg"
                            p={0}
                            w="56px"
                            h="56px"
                            borderRadius="full"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            _hover={{ color: "accent.400", bg: "gray.800" }}
                            _active={{ color: "accent.500", bg: "gray.900" }}
                            aria-label="Share"
                          >
                            <FiShare size={28} />
                          </Button>
                          <Text fontSize="xs" color="gray.400" textAlign="center">Share</Text>
                        </VStack>
                      )}
                    </HStack>
                  )}
                </VStack>
              </>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default KMDeckShareModal;
